/* ==========================================================================
   ahmed.fyi - app.js
   Vanilla motion engine. No dependencies. Loaded with `defer`.
   Every effect is guarded by prefers-reduced-motion (and the theme's
   `enable_motion` setting, surfaced as <html data-motion>) and settles to a
   clean final state when motion is off. Animates transform/opacity/filter only.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  function motionOn() {
    return !reduceMQ.matches && root.getAttribute('data-motion') !== 'false';
  }
  var isTouch = window.matchMedia('(hover: none)').matches;

  /* ---------------------------------------------------------------- theme */
  // No-flash theme is applied inline in <head>; this only wires the toggle.
  function initThemeToggle() {
    var stored = null;
    try { stored = localStorage.getItem('theme'); } catch (e) {}
    if (stored === 'dark' || stored === 'light') root.setAttribute('data-theme', stored);

    function resolved() {
      var t = root.getAttribute('data-theme');
      if (t === 'dark' || t === 'light') return t;
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    document.querySelectorAll('[data-theme-toggle]').forEach(function (btn) {
      if (btn.dataset.toggleWired) return;
      btn.dataset.toggleWired = '1';
      btn.setAttribute('data-scheme', resolved());
      btn.addEventListener('click', function () {
        var next = resolved() === 'dark' ? 'light' : 'dark';
        root.setAttribute('data-theme', next);
        try { localStorage.setItem('theme', next); } catch (e) {}
        document.querySelectorAll('[data-theme-toggle]').forEach(function (b) {
          b.setAttribute('data-scheme', next);
        });
      });
    });
  }

  /* -------------------------------------------------------------- reveals */
  // CSS handles reveals via animation-timeline: view() where supported.
  // JS only steps in as a fallback when that is unavailable.
  function initReveals() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    var supportsTimeline = window.CSS && CSS.supports && CSS.supports('animation-timeline: view()');
    if (supportsTimeline || !motionOn() || !('IntersectionObserver' in window)) return;

    root.classList.add('reveal-js');
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* --------------------------------------------------- typewriter headline */
  // Types each word, holds, deletes, then types the next. The first word is
  // server-rendered in .rot__live, so with no JS (or reduced motion) it stays put.
  function initTypewriter() {
    document.querySelectorAll('.rot[data-typewriter]').forEach(function (rot) {
      if (rot.dataset.twInit) return; // idempotent re-init (theme editor)
      rot.dataset.twInit = '1';
      var live = rot.querySelector('.rot__live');
      var dataEl = rot.querySelector('.rot__data');
      if (!live || !dataEl) return;
      var words;
      try { words = JSON.parse(dataEl.textContent) || []; } catch (e) { words = []; }
      if (words.length < 2 || !motionOn()) { if (words[0]) live.textContent = words[0]; return; }

      rot.classList.add('rot--typing');
      var wi = 0, ci = 0, deleting = false;
      var TYPE = 78, DEL = 42, HOLD = 1500, GAP = 380;
      live.textContent = '';

      function step() {
        var word = words[wi];
        if (!deleting) {
          ci++;
          live.textContent = word.slice(0, ci);
          if (ci >= word.length) { deleting = true; setTimeout(step, HOLD); return; }
          setTimeout(step, TYPE);
        } else {
          ci--;
          live.textContent = word.slice(0, ci);
          if (ci <= 0) { deleting = false; wi = (wi + 1) % words.length; setTimeout(step, GAP); return; }
          setTimeout(step, DEL);
        }
      }
      setTimeout(step, 500);
    });
  }

  /* ----------------------------------------------------------- magnetic */
  function initMagnetic() {
    if (!motionOn() || isTouch) return;
    document.querySelectorAll('[data-magnetic]').forEach(function (el) {
      if (el.dataset.magInit) return;
      el.dataset.magInit = '1';
      var max = 6, raf = null, lastEvent = null;
      el.addEventListener('pointermove', function (e) {
        lastEvent = e;
        if (raf !== null) return; // rAF-throttled: one layout read per frame
        raf = requestAnimationFrame(function () {
          raf = null;
          var r = el.getBoundingClientRect();
          var dx = (lastEvent.clientX - (r.left + r.width / 2)) / (r.width / 2);
          var dy = (lastEvent.clientY - (r.top + r.height / 2)) / (r.height / 2);
          el.style.transform = 'translate(' + (dx * max).toFixed(2) + 'px,' + (dy * max).toFixed(2) + 'px) scale(1.03)';
        });
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* ----------------------------------------------------------- count-up */
  function initCountups() {
    var els = document.querySelectorAll('[data-countup]');
    if (!els.length) return;

    function finalText(el) {
      var to = parseFloat(el.getAttribute('data-to')) || 0;
      var dp = (el.getAttribute('data-to') || '').split('.')[1];
      var val = dp ? to.toFixed(dp.length) : String(Math.round(to));
      return (el.getAttribute('data-prefix') || '') + val + (el.getAttribute('data-suffix') || '');
    }
    if (!motionOn() || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.textContent = finalText(el); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target; io.unobserve(el);
        if (el.dataset.counted) return; // idempotent re-init (theme editor)
        el.dataset.counted = '1';
        var to = parseFloat(el.getAttribute('data-to')) || 0;
        var dp = (el.getAttribute('data-to') || '').split('.')[1];
        var decimals = dp ? dp.length : 0;
        var pre = el.getAttribute('data-prefix') || '';
        var suf = el.getAttribute('data-suffix') || '';
        var dur = 1200, start = null;
        function tick(ts) {
          if (start === null) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          var cur = to * eased;
          el.textContent = pre + (decimals ? cur.toFixed(decimals) : Math.round(cur)) + suf;
          if (p < 1) requestAnimationFrame(tick);
        }
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------ marquee */
  function initMarquees() {
    document.querySelectorAll('.marquee').forEach(function (m) {
      var track = m.querySelector('.marquee__track');
      if (!track || track.dataset.cloned) return;
      // Duplicate content so the -50% translate loops seamlessly.
      // The clone is aria-hidden so screen readers hear the list once.
      var clone = document.createElement('span');
      clone.setAttribute('aria-hidden', 'true');
      Array.prototype.slice.call(track.childNodes).forEach(function (n) {
        clone.appendChild(n.cloneNode(true));
      });
      track.appendChild(clone);
      track.dataset.cloned = '1';
    });
  }

  /* ---------------------------------------------------- command palette */
  function initCmdK() {
    var wired = false, dialog = null, input = null, list = null, items = [], filtered = [], active = 0;

    function loadItems() {
      var src = document.getElementById('cmdk-items');
      if (!src) return [];
      try { return JSON.parse(src.textContent) || []; } catch (e) { return []; }
    }
    function build() {
      items = loadItems();
      dialog = document.createElement('dialog');
      dialog.id = 'cmdk';
      dialog.setAttribute('aria-label', 'Command palette');
      dialog.innerHTML =
        '<input class="cmdk-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search or jump to…" aria-label="Search">' +
        '<ul class="cmdk-list" role="listbox"></ul>';
      document.body.appendChild(dialog);
      input = dialog.querySelector('.cmdk-search');
      list = dialog.querySelector('.cmdk-list');
      input.addEventListener('input', function () { render(input.value); });
      input.addEventListener('keydown', onKeys);
      dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });
      dialog.addEventListener('close', function () {});
      render('');
    }
    function render(q) {
      q = (q || '').trim().toLowerCase();
      filtered = q ? items.filter(function (it) { return (it.label || '').toLowerCase().indexOf(q) > -1; }) : items.slice();
      active = 0;
      var rows = filtered.map(function (it) {
        return '<li role="option"><a href="' + encodeURI(it.url || '#') + '">' +
          '<span>' + escapeHtml(it.label || '') + '</span>' +
          (it.hint ? '<span class="hint">' + escapeHtml(it.hint) + '</span>' : '') + '</a></li>';
      });
      // Always offer full-site search as the last row when a query is typed.
      if (q) {
        rows.push('<li role="option"><a href="/search?q=' + encodeURIComponent(q) + '">' +
          '<span>Search the site for "' + escapeHtml(q) + '"</span>' +
          '<span class="hint">Enter</span></a></li>');
      }
      if (!rows.length) { list.innerHTML = '<li class="cmdk-empty">Type to search</li>'; return; }
      list.innerHTML = rows.join('');
      setActive(0);
    }
    function setActive(n) {
      var opts = list.querySelectorAll('li[role="option"]');
      if (!opts.length) return;
      active = (n + opts.length) % opts.length;
      opts.forEach(function (li, i) {
        if (i === active) { li.setAttribute('aria-selected', 'true'); li.scrollIntoView({ block: 'nearest' }); }
        else li.setAttribute('aria-selected', 'false');
      });
    }
    function onKeys(e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        var links = list.querySelectorAll('li[role="option"] a');
        if (links[active]) { window.location.href = links[active].getAttribute('href'); }
        else if (input.value.trim()) { window.location.href = '/search?q=' + encodeURIComponent(input.value.trim()); }
      }
    }
    function open() {
      if (!dialog) build();
      render('');
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      input.value = ''; input.focus();
    }
    function close() { if (dialog && dialog.open) dialog.close(); }

    function trigger(e) {
      if (e) e.preventDefault();
      open();
    }
    function firstInteract(e) {
      if (!wired) { wired = true; } // build happens lazily in open()
      trigger(e);
    }
    // Import-on-interaction: only build the palette on first use.
    document.querySelectorAll('[data-cmdk]').forEach(function (btn) {
      btn.addEventListener('click', firstInteract);
    });
    document.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        firstInteract(e);
      } else if (e.key === 'Escape' && dialog && dialog.open) {
        close();
      }
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ------------------------------------------------ glass pill on scroll */
  // Toggle .is-scrolled once past 40px; rAF-throttled. Runs regardless of
  // motion preference (the CSS transition is what reduced-motion disables).
  function initHeaderScroll() {
    var header = document.querySelector('[data-header]');
    if (!header || header.dataset.scrollWired) return;
    header.dataset.scrollWired = '1';
    var ticking = false;
    function update() {
      ticking = false;
      if ((window.scrollY || window.pageYOffset || 0) > 40) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* --------------------------------------------------- dropdown submenus */
  // Desktop uses CSS hover/focus-within; JS adds click (touch/mobile) toggling
  // with aria-expanded, closes siblings, and closes on outside click / Escape.
  function initDropdowns() {
    var items = document.querySelectorAll('.nav-item.has-sub');
    if (!items.length) return;
    function closeAll(except) {
      items.forEach(function (item) {
        if (item === except) return;
        item.classList.remove('open');
        var b = item.querySelector('.nav-link');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }
    items.forEach(function (item) {
      var btn = item.querySelector('.nav-link');
      if (!btn || item.dataset.subWired) return;
      item.dataset.subWired = '1';
      // Click toggles .open (touch / explicit).
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var isOpen = item.classList.toggle('open');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) closeAll(item);
      });
      // The submenu is also revealed by CSS :hover / :focus-within, so keep
      // aria-expanded in sync with that visual state (WCAG 4.1.2).
      var expand = function () { btn.setAttribute('aria-expanded', 'true'); };
      var collapse = function (e) {
        if (item.classList.contains('open')) return;
        if (e && e.relatedTarget && item.contains(e.relatedTarget)) return;
        if (item.contains(document.activeElement)) return;
        btn.setAttribute('aria-expanded', 'false');
      };
      item.addEventListener('mouseenter', expand);
      item.addEventListener('mouseleave', collapse);
      item.addEventListener('focusin', expand);
      item.addEventListener('focusout', collapse);
    });
    // Bind the document-level closers once; query fresh so section re-renders stay correct.
    if (!document.documentElement.dataset.subDocWired) {
      document.documentElement.dataset.subDocWired = '1';
      var closeAllGlobal = function () {
        document.querySelectorAll('.nav-item.has-sub').forEach(function (item) {
          item.classList.remove('open');
          var b = item.querySelector('.nav-link');
          if (b && !item.matches(':hover') && !item.contains(document.activeElement)) {
            b.setAttribute('aria-expanded', 'false');
          }
        });
      };
      document.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.nav-item.has-sub')) return;
        closeAllGlobal();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllGlobal();
      });
    }
  }

  /* ---------------------------------------------------- mobile drawer */
  function initDrawer() {
    var drawer = document.querySelector('[data-drawer]');
    var opener = document.querySelector('[data-drawer-open]');
    if (!drawer || !opener || drawer.dataset.wired) return;
    drawer.dataset.wired = '1';
    var closeT = null;
    var prevOverflow = '';

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      // Trap focus inside the open drawer (aria-modal contract).
      var els = drawer.querySelectorAll(FOCUSABLE);
      if (!els.length) return;
      var first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    }
    function open() {
      if (closeT) { clearTimeout(closeT); closeT = null; }
      drawer.hidden = false;
      void drawer.offsetWidth; // reflow so the slide-in transition runs
      drawer.classList.add('is-open');
      opener.setAttribute('aria-expanded', 'true');
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('[data-drawer-close]');
      if (first) first.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      drawer.classList.remove('is-open');
      opener.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      closeT = setTimeout(function () { drawer.hidden = true; }, 380);
      opener.focus();
    }

    opener.addEventListener('click', open);
    drawer.querySelectorAll('[data-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    // A tap on a drawer link navigates; unlock scroll on the way out.
    drawer.querySelectorAll('.drawer__nav a').forEach(function (a) {
      a.addEventListener('click', close);
    });
  }


  /* -------------------------------------------------------- cursor light */
  // A large soft radial glow lerps toward the pointer and plays with light on
  // the page (multiply on light, screen on dark — handled in CSS). Fine
  // pointers only; native cursor stays visible; off on touch + reduced motion.
  function initCursor() {
    if (isTouch || !motionOn()) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (document.querySelector('.cursor-glow')) return;
    var glow = document.createElement('div');
    glow.className = 'cursor-glow';
    glow.setAttribute('aria-hidden', 'true');
    document.body.appendChild(glow);
    var x = 0, y = 0, gx = 0, gy = 0, seen = false, raf = null;
    function loop() {
      gx += (x - gx) * 0.12; gy += (y - gy) * 0.12;
      glow.style.transform = 'translate3d(' + gx.toFixed(1) + 'px,' + gy.toFixed(1) + 'px,0)';
      raf = requestAnimationFrame(loop);
    }
    document.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      x = e.clientX; y = e.clientY;
      if (!seen) { seen = true; gx = x; gy = y; glow.classList.add('is-visible'); if (raf === null) raf = requestAnimationFrame(loop); }
    }, { passive: true });
    document.addEventListener('mouseleave', function () { glow.classList.remove('is-visible'); }, { passive: true });
    document.addEventListener('mouseenter', function () { if (seen) glow.classList.add('is-visible'); }, { passive: true });
  }

  /* --------------------------------------------------------------- boot */
  function boot() {
    initThemeToggle();
    initHeaderScroll();
    initDropdowns();
    initDrawer();
    initReveals();
    initTypewriter();
    initMagnetic();
    initCountups();
    initMarquees();
    initCmdK();
    initCursor();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: section HTML is re-rendered on edit without a page load, so
  // re-run the idempotent initializers (each guards against double-binding).
  document.addEventListener('shopify:section:load', function () {
    initReveals();
    initTypewriter();
    initCountups();
    initMarquees();
    initHeaderScroll();
    initDropdowns();
    initDrawer();
    initThemeToggle();
  });
})();
